// Property inventory templates — the backbone of the move-in / move-out
// settlement flow.
//
// Why this exists: rental disputes in India are almost never about "was
// there a photo" — they're about "what changed, and who pays". A photo
// album can't answer that. A per-ITEM record with a condition can.
//
// The owner picks a furnishing level; we pre-load the items a home at that
// level actually has, so they curate a list instead of authoring one. Every
// item carries its own condition + photos, at move-in and again at move-out,
// which makes the settlement a diff rather than an argument.
//
// Room keys deliberately reuse the existing condition-photo categories
// (hall/bedroom/kitchen/bathroom/meter — see LeaseConditionPhotos.js and
// server/routes/vacate.js REQUIRED_PHOTO_CATEGORIES) so inventory photos and
// room photos line up in the same comparison view. `utility` is inventory-only.

export const FURNISHING_LEVELS = [
    { key: "unfurnished", label: "Unfurnished", hint: "Fixtures and fittings only" },
    { key: "semi", label: "Semi-furnished", hint: "Plus fans, lights, wardrobes, geyser" },
    { key: "furnished", label: "Furnished", hint: "Move-in ready, appliances included" },
];

// Condition scale. "Worn" matters legally: fair wear and tear is normally the
// OWNER's cost, not the tenant's — separating it from "Damaged" is what keeps
// a settlement defensible instead of merely opinionated.
export const CONDITIONS = [
    { key: "good", label: "Good", tone: "gn" },
    { key: "worn", label: "Worn", tone: "wn", note: "Fair wear and tear" },
    { key: "damaged", label: "Damaged", tone: "rd" },
    { key: "missing", label: "Missing", tone: "rd" },
];

export const CONDITION_MAP = CONDITIONS.reduce((m, c) => { m[c.key] = c; return m; }, {});

// levels: which furnishing levels ship this item by default.
const ALL = ["unfurnished", "semi", "furnished"];
const SEMI_UP = ["semi", "furnished"];
const FURN = ["furnished"];

export const INVENTORY_ROOMS = [
    {
        key: "hall", label: "Hall / Living", icon: "🛋️",
        items: [
            { name: "Main door & lock", levels: ALL },
            { name: "Windows & grills", levels: ALL },
            { name: "Light fittings", levels: ALL },
            { name: "Switch boards & sockets", levels: ALL },
            { name: "Ceiling fan", levels: SEMI_UP },
            { name: "Curtain rods", levels: SEMI_UP },
            { name: "Sofa set", levels: FURN },
            { name: "TV unit", levels: FURN },
        ],
    },
    {
        key: "bedroom", label: "Bedroom", icon: "🛏️",
        items: [
            { name: "Door & lock", levels: ALL },
            { name: "Windows & grills", levels: ALL },
            { name: "Light fittings", levels: ALL },
            { name: "Switch boards & sockets", levels: ALL },
            { name: "Ceiling fan", levels: SEMI_UP },
            { name: "Wardrobe / cupboard", levels: SEMI_UP },
            { name: "Bed & mattress", levels: FURN },
            { name: "Air conditioner", levels: FURN },
        ],
    },
    {
        key: "kitchen", label: "Kitchen", icon: "🍳",
        items: [
            { name: "Sink & tap", levels: ALL },
            { name: "Kitchen platform / slab", levels: ALL },
            { name: "Drainage & pipes", levels: ALL },
            { name: "Gas pipeline point", levels: ALL },
            { name: "Lights & switches", levels: ALL },
            { name: "Cabinets & shutters", levels: SEMI_UP },
            { name: "Exhaust fan", levels: SEMI_UP },
            { name: "Chimney", levels: FURN },
            { name: "Stove / hob", levels: FURN },
            { name: "Water purifier", levels: FURN },
        ],
    },
    {
        key: "bathroom", label: "Bathroom", icon: "🚿",
        items: [
            { name: "Door & latch", levels: ALL },
            { name: "Wash basin", levels: ALL },
            { name: "WC / commode", levels: ALL },
            { name: "Taps & mixer", levels: ALL },
            { name: "Shower", levels: ALL },
            { name: "Health faucet", levels: ALL },
            { name: "Drainage & pipes", levels: ALL },
            { name: "Light & switch", levels: ALL },
            { name: "Exhaust fan", levels: SEMI_UP },
            { name: "Geyser", levels: SEMI_UP },
            { name: "Mirror & shelf", levels: SEMI_UP },
        ],
    },
    {
        key: "utility", label: "Utility / Common", icon: "🔧",
        items: [
            { name: "Water motor / pump", levels: ALL },
            { name: "Overhead tank", levels: ALL },
            { name: "Sump", levels: ALL },
            { name: "Washing machine point", levels: ALL },
            { name: "Balcony grills", levels: ALL },
            { name: "Inverter / stabiliser", levels: FURN },
        ],
    },
    {
        key: "meter", label: "Meters & Readings", icon: "📊",
        items: [
            { name: "Electricity meter reading", levels: ALL, reading: true },
            { name: "Water meter reading", levels: ALL, reading: true },
            { name: "Gas cylinder", levels: FURN },
        ],
    },
];

let _seq = 0;
const newId = () => `inv_${Date.now().toString(36)}_${(_seq++).toString(36)}${Math.random().toString(36).slice(2, 5)}`;

/**
 * Build a fresh inventory for a furnishing level.
 * Every item starts "good" — the owner downgrades what isn't.
 */
export function buildInventory(level) {
    const lvl = FURNISHING_LEVELS.some(f => f.key === level) ? level : "semi";
    return {
        furnishing: lvl,
        created_at: new Date().toISOString(),
        rooms: INVENTORY_ROOMS.map(room => ({
            key: room.key,
            label: room.label,
            icon: room.icon,
            items: room.items
                .filter(it => it.levels.includes(lvl))
                .map(it => ({
                    id: newId(),
                    name: it.name,
                    qty: 1,
                    condition: "good",
                    note: "",
                    reading: it.reading ? "" : undefined,
                    photos: [],
                })),
        })).filter(r => r.items.length > 0),
    };
}

/** Add a custom item to a room (owner can always extend the template). */
export function makeItem(name) {
    return { id: newId(), name: String(name || "").trim() || "Item", qty: 1, condition: "good", note: "", photos: [] };
}

/** Flat counts for headers/summaries. */
export function inventoryStats(inv) {
    const rooms = (inv && Array.isArray(inv.rooms)) ? inv.rooms : [];
    let items = 0, photos = 0, flagged = 0, documented = 0;
    for (const r of rooms) {
        for (const it of (r.items || [])) {
            items++;
            const n = (it.photos || []).length;
            photos += n;
            if (n > 0) documented++;
            if (it.condition === "damaged" || it.condition === "missing") flagged++;
        }
    }
    return { items, photos, flagged, documented, rooms: rooms.length };
}

/** Tolerant read of lease.terms.inventory (terms is TEXT in the DB). */
export function leaseInventory(lease) {
    if (!lease) return null;
    try {
        const terms = typeof lease.terms === "string" ? JSON.parse(lease.terms) : (lease.terms || {});
        const inv = terms.inventory;
        return inv && Array.isArray(inv.rooms) ? inv : null;
    } catch (_) {
        return null;
    }
}
